import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Icon, Collapse } from 'ck-ui';
import { getContrastText, emphasize } from 'ck-utils/colors';
import { useSash } from './hooks/useSash';
import styles from './ProductPDPCalloutSash.module.scss';

const OptionalLink = ({
  url,
  children,
  className,
  ...props
}) => {
  if (url) {
    return (
      <a href={url} {...props} className={className}>
        {children}
      </a>
    );
  }

  return <>{children}</>;
};

OptionalLink.propTypes = {
  url: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const Callout = ({ pdpSash, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { callout, expandablePanel } = pdpSash;

  const rootId = `pdp-callout-${index}`;

  if (!callout && !expandablePanel) return null;

  return (
    <div id={rootId} className={styles.root} aria-expanded={isExpanded} aria-haspopup={!!expandablePanel}>
      {callout && (
        <Collapse in={!isExpanded} className={classnames(styles.callout, { [styles.noPaddingBottom]: callout.imagePath && !callout.text })}>
          <>
            {Boolean(callout.title) && <h2 className="body-medium"><b>{callout.title}</b></h2>}
            {Boolean(callout.imagePath) && (
              <OptionalLink url={callout.imageLink} title={callout.title}>
                <img
                  className={classnames(styles.image, callout.title ? 'mt-2' : styles.imageNoTitle)}
                  src={`https://culture-kings-merch.imgix.net/${callout.imagePath}?auto=format,compress&w=800`}
                  alt={callout.title}
                />
              </OptionalLink>
            )}
            {Boolean(callout.text) && (
              // eslint-disable-next-line react/no-danger
              <div className="mt-2" dangerouslySetInnerHTML={{ __html: callout.text }} />
            )}
          </>
        </Collapse>
      )}
      {expandablePanel && (
        <div className={styles.expandablePanel}>
          <div
            style={{
              backgroundColor: `#${expandablePanel.color}`,
              color: getContrastText(`#${expandablePanel.color}`),
              borderBottomWidth: isExpanded ? 1 : 0,
              borderBottomColor: emphasize(`#${expandablePanel.color}`),
            }}
            aria-controls={rootId}
            role="button"
            tabIndex={0}
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.expandablePanelTitle}
          >
            <span className={styles.calloutTitle}>{expandablePanel.title}</span>
            <Icon name={isExpanded ? 'arrow-up' : 'arrow-down'} color="secondary" />
          </div>
          <Collapse
            in={isExpanded}
            className={styles.expandablePanel}
            style={{ backgroundColor: `#${expandablePanel.color}`, color: getContrastText(`#${expandablePanel.color}`) }}
          >
            <div className={styles.expandablePanelText}>
              { /* eslint-disable-next-line react/no-danger */ }
              <div dangerouslySetInnerHTML={{ __html: expandablePanel.text }} />
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

Callout.propTypes = {
  pdpSash: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

const ProductPDPCalloutSash = ({ isBelowAddToCart, tags, extraTags }) => {
  const sash = useSash([...tags, ...extraTags]);
  const pdpSashes = sash.pdpSash.filter(pdpSash => (isBelowAddToCart ? pdpSash.isBelowAddToCart === isBelowAddToCart : !pdpSash.isBelowAddToCart));
  const exclusivePdpSash = pdpSashes.find(pdpSash => (pdpSash.isExclusivePDP));
  if (exclusivePdpSash) return <Callout pdpSash={exclusivePdpSash} index={0} />;
  return pdpSashes.map((pdpSash, index) => (
    <Callout pdpSash={pdpSash} index={index} key={JSON.stringify(pdpSash)} />
  ));
};

ProductPDPCalloutSash.propTypes = {
  isBelowAddToCart: PropTypes.bool,
  tags: PropTypes.array.isRequired,
  extraTags: PropTypes.array,
};

ProductPDPCalloutSash.defaultProps = {
  isBelowAddToCart: false,
  extraTags: []
};

export { ProductPDPCalloutSash };
